import { useCallback, useEffect } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  TechDocsAddonLocations,
  useTechDocsReaderPage,
} from '@backstage/plugin-techdocs-react';
import { AddonBlueprint } from '@backstage/plugin-techdocs-react/alpha';

const desktopMediaQuery = 'screen and (min-width: 76.1875em)';
const appHeaderSelector = '.tegel-app-header';
const footerSelector = '.tegel-app-footer';
const techDocsContainerSelector = '.md-container';
const techDocsFooterSelector = '.md-container > .md-footer';
const techDocsSidebarSelector = '.md-sidebar';
const techDocsSidebarScrollWrapSelector = '.md-sidebar__scrollwrap';
const techDocsTabsSelector = '.md-container > .md-tabs';
const techDocsShadowStyleId = 'driveline-techdocs-shadow-style';
const sidebarFooterGap = 24;
const sidebarViewportGap = 16;

const techDocsShadowStyles = `
:host {
  --driveline-shadow-surface: var(--tds-background-primary, #ffffff);
  --driveline-shadow-scrollbar-thumb: #8c9299;
  --driveline-shadow-scrollbar-thumb-hover: #6b7280;
  --driveline-shadow-scrollbar-track: #f6f7f8;
  --driveline-shadow-sidebar-height: calc(
    100vh - var(--driveline-shadow-sidebar-top) - 24px
  );
  --driveline-shadow-sidebar-top: calc(
    var(--tds-header-height, 64px) + 168px
  );
  --driveline-shadow-scroll-margin-top: calc(var(--tds-header-height, 64px) + 16px);
  background: var(--driveline-shadow-surface);
  display: block;
  max-width: 100%;
  min-height: min(680px, max(420px, calc(100vh - 220px)));
  min-width: 0;
  overflow: visible !important;
}

:host-context(.tds-mode-dark),
:host-context(body.tds-mode-dark) {
  --driveline-shadow-surface: var(--driveline-bg-surface, #15181d);
  --driveline-shadow-scrollbar-thumb: #5f6875;
  --driveline-shadow-scrollbar-thumb-hover: #7b8796;
  --driveline-shadow-scrollbar-track: #0b0c0f;
}

.md-container,
.md-main,
.md-main__inner,
.md-content,
.md-content__inner,
.md-sidebar {
  background: var(--driveline-shadow-surface);
}

.md-container,
.md-main,
.md-main__inner,
.md-content,
.md-content__inner {
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
}

.md-main__inner.md-grid,
.md-footer__inner.md-grid {
  width: 100%;
}

.md-sidebar--primary,
.md-sidebar--secondary,
.md-sidebar--primary .md-sidebar__scrollwrap,
.md-sidebar--secondary .md-sidebar__scrollwrap {
  background: var(--driveline-shadow-surface);
}

.md-sidebar--primary,
.md-sidebar--secondary {
  z-index: 3;
}

.md-sidebar--secondary .md-sidebar__scrollwrap {
  border-left: 1px solid rgba(22, 26, 32, 0.12);
  box-sizing: border-box;
}

.md-nav__title,
.md-nav__title::before,
.md-nav__title::after {
  background: var(--driveline-shadow-surface) !important;
  background-image: none !important;
  box-shadow: none !important;
}

.md-nav__title {
  border: 0 !important;
}

:host-context(.tds-mode-dark) .md-sidebar--secondary .md-sidebar__scrollwrap,
:host-context(body.tds-mode-dark) .md-sidebar--secondary .md-sidebar__scrollwrap {
  border-left-color: rgba(237, 239, 243, 0.18);
}

.md-main__inner,
.md-content,
.md-content__inner,
.md-typeset {
  min-width: 0;
}

.md-content {
  margin-bottom: 0 !important;
  overflow-x: clip;
}

.md-content__inner {
  margin-bottom: 0 !important;
  padding-bottom: 1.25rem;
}

@media ${desktopMediaQuery} {
  .md-sidebar--primary,
  .md-sidebar--secondary {
    bottom: auto !important;
    height: var(--driveline-shadow-sidebar-height) !important;
    max-height: var(--driveline-shadow-sidebar-height) !important;
    top: var(--driveline-shadow-sidebar-top) !important;
  }

  .md-main__inner.md-grid:has(.md-sidebar--secondary) .md-content {
    margin-right: calc(16rem + 1.5rem);
    width: auto;
  }

  .md-main__inner.md-grid:has(.md-sidebar--secondary) .md-content__inner {
    box-sizing: border-box;
    max-width: 100%;
  }

  .md-main__inner.md-grid:has(.md-sidebar--secondary) .md-sidebar--secondary {
    left: auto;
    right: 1.5rem;
  }
}

.md-content__button {
  display: none !important;
}

.md-typeset img,
.md-typeset svg,
.md-typeset video {
  max-width: 100%;
}

.md-typeset .highlight,
.md-typeset .highlighttable,
.md-typeset pre {
  display: block;
  max-width: 100%;
  overflow-x: auto;
}

.md-typeset pre code {
  overflow-wrap: normal;
  white-space: pre;
  word-break: normal;
}

.md-typeset__scrollwrap,
.md-typeset__table {
  box-sizing: border-box;
  display: block;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

.md-typeset__table table {
  max-width: none;
  min-width: 100%;
  width: max-content;
}

.md-typeset > table,
.md-typeset .tabbed-content > table,
.md-typeset .admonition > table {
  display: block;
  max-width: 100%;
  overflow-x: auto;
  width: max-content;
}

.md-typeset table th,
.md-typeset table td {
  overflow-wrap: normal;
  word-break: normal;
}

.md-content [id] {
  scroll-margin-top: var(--driveline-shadow-scroll-margin-top);
}

.md-footer {
  background: var(--driveline-shadow-surface);
  border-top: 1px solid rgba(22, 26, 32, 0.16);
  position: static !important;
  width: 100%;
}

.md-footer__inner {
  box-sizing: border-box;
  max-width: none !important;
  overflow-x: hidden;
  padding: 0.4rem 0.8rem 0.8rem;
}

:host-context(.tds-mode-dark) .md-footer,
:host-context(body.tds-mode-dark) .md-footer {
  border-top-color: rgba(237, 239, 243, 0.18);
}

.md-sidebar__scrollwrap,
.md-nav,
.md-content,
.md-main {
  scrollbar-color: var(--driveline-shadow-scrollbar-thumb)
    var(--driveline-shadow-scrollbar-track);
  scrollbar-width: thin;
}

.md-sidebar__scrollwrap::-webkit-scrollbar,
.md-nav::-webkit-scrollbar,
.md-content::-webkit-scrollbar,
.md-main::-webkit-scrollbar {
  height: 10px;
  width: 10px;
}

.md-sidebar__scrollwrap::-webkit-scrollbar-track,
.md-nav::-webkit-scrollbar-track,
.md-content::-webkit-scrollbar-track,
.md-main::-webkit-scrollbar-track {
  background: var(--driveline-shadow-scrollbar-track);
}

.md-sidebar__scrollwrap::-webkit-scrollbar-thumb,
.md-nav::-webkit-scrollbar-thumb,
.md-content::-webkit-scrollbar-thumb,
.md-main::-webkit-scrollbar-thumb {
  background: var(--driveline-shadow-scrollbar-thumb);
  border: 2px solid var(--driveline-shadow-scrollbar-track);
  border-radius: 999px;
}

.md-sidebar__scrollwrap::-webkit-scrollbar-thumb:hover,
.md-nav::-webkit-scrollbar-thumb:hover,
.md-content::-webkit-scrollbar-thumb:hover,
.md-main::-webkit-scrollbar-thumb:hover {
  background: var(--driveline-shadow-scrollbar-thumb-hover);
}
`;

const elementTop = (element: Element | null | undefined) =>
  element instanceof HTMLElement
    ? element.getBoundingClientRect().top
    : undefined;

const elementBottom = (element: Element | null | undefined) =>
  element instanceof HTMLElement
    ? element.getBoundingClientRect().bottom
    : undefined;

const topWithinViewport = (
  top: number | undefined,
  viewportHeight: number,
): top is number => typeof top === 'number' && top >= 0 && top < viewportHeight;

const DrivelineTechDocsFooterClearanceAddon = () => {
  const { shadowRoot } = useTechDocsReaderPage();

  useEffect(() => {
    if (!shadowRoot) {
      return undefined;
    }

    const styleElement =
      shadowRoot.querySelector<HTMLStyleElement>(`#${techDocsShadowStyleId}`) ??
      document.createElement('style');

    styleElement.id = techDocsShadowStyleId;
    styleElement.textContent = techDocsShadowStyles;

    if (!styleElement.isConnected) {
      shadowRoot.appendChild(styleElement);
    }

    return () => {
      styleElement.remove();
    };
  }, [shadowRoot]);

  const resetSidebarBounds = useCallback(() => {
    const docsRoot = shadowRoot?.querySelector(techDocsContainerSelector);
    if (!(docsRoot instanceof HTMLElement)) {
      return;
    }

    const sidebars = Array.from(
      docsRoot.querySelectorAll<HTMLElement>(techDocsSidebarSelector),
    );

    sidebars.forEach(sidebar => {
      sidebar.style.removeProperty('top');
      sidebar.style.removeProperty('bottom');
      sidebar.style.removeProperty('height');

      const scrollWraps = Array.from(
        sidebar.querySelectorAll<HTMLElement>(
          techDocsSidebarScrollWrapSelector,
        ),
      );

      scrollWraps.forEach(scrollWrap => {
        scrollWrap.style.removeProperty('height');
        scrollWrap.style.removeProperty('max-height');
        scrollWrap.style.removeProperty('overflow-x');
        scrollWrap.style.removeProperty('padding-bottom');
      });
    });
  }, [shadowRoot]);

  const updateSidebarBounds = useCallback(() => {
    const docsRoot = shadowRoot?.querySelector(techDocsContainerSelector);
    if (!(docsRoot instanceof HTMLElement)) {
      return;
    }

    if (
      typeof window.matchMedia !== 'function' ||
      !window.matchMedia(desktopMediaQuery).matches
    ) {
      resetSidebarBounds();
      return;
    }

    const sidebars = Array.from(
      docsRoot.querySelectorAll<HTMLElement>(techDocsSidebarSelector),
    );
    if (!sidebars.length) {
      return;
    }

    const tabsHeight =
      docsRoot
        .querySelector<HTMLElement>(techDocsTabsSelector)
        ?.getBoundingClientRect().height ?? 0;
    const docsTop = Math.max(
      elementTop(docsRoot) ?? 0,
      elementTop(shadowRoot?.host) ?? 0,
      0,
    );
    const appHeaderBottom =
      elementBottom(document.querySelector(appHeaderSelector)) ?? 0;
    const viewportTopClearance =
      Math.max(appHeaderBottom, 0) + sidebarViewportGap;
    const sidebarTop = Math.max(docsTop + tabsHeight, viewportTopClearance);
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;

    const appFooterTop = elementTop(document.querySelector(footerSelector));
    const docsFooterTop = elementTop(
      docsRoot.querySelector(techDocsFooterSelector),
    );
    const visibleFooterTops = [appFooterTop, docsFooterTop].filter(
      (top): top is number => topWithinViewport(top, viewportHeight),
    );
    const footerBoundary =
      visibleFooterTops.length > 0
        ? Math.min(viewportHeight, ...visibleFooterTops) - sidebarFooterGap
        : viewportHeight;
    const sidebarEnd = Math.max(sidebarTop, footerBoundary);
    const sidebarHeight = Math.max(0, sidebarEnd - sidebarTop);
    docsRoot.style.setProperty(
      '--driveline-shadow-sidebar-height',
      `${sidebarHeight}px`,
    );
    docsRoot.style.setProperty(
      '--driveline-shadow-sidebar-top',
      `${sidebarTop}px`,
    );

    sidebars.forEach(sidebar => {
      const nextTop = `${sidebarTop}px`;
      const nextHeight = `${sidebarHeight}px`;

      sidebar.style.setProperty('top', nextTop, 'important');
      sidebar.style.setProperty('bottom', 'auto', 'important');
      sidebar.style.setProperty('height', nextHeight, 'important');

      const scrollWraps = Array.from(
        sidebar.querySelectorAll<HTMLElement>(
          techDocsSidebarScrollWrapSelector,
        ),
      );

      scrollWraps.forEach(scrollWrap => {
        scrollWrap.style.setProperty('height', '100%', 'important');
        scrollWrap.style.setProperty('max-height', '100%', 'important');
        scrollWrap.style.setProperty('overflow-x', 'hidden', 'important');
        scrollWrap.style.setProperty('padding-bottom', '12px', 'important');
      });
    });
  }, [resetSidebarBounds, shadowRoot]);

  useEffect(() => {
    if (!shadowRoot) {
      return undefined;
    }

    let animationFrame: number | undefined;
    const scheduleUpdate = () => {
      if (animationFrame !== undefined) {
        return;
      }
      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = undefined;
        updateSidebarBounds();
      });
    };

    scheduleUpdate();
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('load', scheduleUpdate);
    const retryInterval = window.setInterval(scheduleUpdate, 100);
    const retryTimeout = window.setTimeout(() => {
      window.clearInterval(retryInterval);
    }, 3000);

    const observer =
      typeof MutationObserver === 'function'
        ? new MutationObserver(scheduleUpdate)
        : undefined;
    observer?.observe(shadowRoot, {
      attributeFilter: ['class', 'style'],
      attributes: true,
      childList: true,
      subtree: true,
    });
    const resizeObserver =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(scheduleUpdate)
        : undefined;
    if (shadowRoot.host instanceof Element) {
      resizeObserver?.observe(shadowRoot.host);
    }
    const docsRoot = shadowRoot.querySelector(techDocsContainerSelector);
    if (docsRoot instanceof Element) {
      resizeObserver?.observe(docsRoot);
    }

    return () => {
      if (animationFrame !== undefined) {
        window.cancelAnimationFrame(animationFrame);
      }
      window.clearInterval(retryInterval);
      window.clearTimeout(retryTimeout);
      observer?.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('load', scheduleUpdate);
    };
  }, [shadowRoot, updateSidebarBounds]);

  return null;
};

const techDocsFooterClearanceAddon = AddonBlueprint.make({
  name: 'driveline-footer-clearance',
  params: {
    name: 'DrivelineFooterClearance',
    location: TechDocsAddonLocations.Content,
    component: DrivelineTechDocsFooterClearanceAddon,
  },
});

export const techDocsFooterClearanceAddonModule = createFrontendModule({
  pluginId: 'techdocs',
  extensions: [techDocsFooterClearanceAddon],
});
